import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { faReply } from '@fortawesome/pro-light-svg-icons/faReply';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { outreachReplyPath, outreachReviewPath } from '../../outreachPaths';
import type { PeopleRow, SentPitch } from '../../outreachTypes';
import { liveMessages } from '../liveMessages';
import { peopleMessages } from '../peopleMessages';
import { PitchTimelineList } from './PitchTimelineList';
import { pitchHero } from './pitchHero';
import { newestReply, pitchTimeline } from './pitchTimeline';
import { detailsMessages } from './detailsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the details panel */
import styles from './Details.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PitchStatusCardProps {
  row: PeopleRow;
  pitch: SentPitch | undefined;
  name: string;
  campaignUuid: string;
  disabled: boolean;
  onStopFollowUp: (messageUuid: string) => void;
}

/**
 * The status card of the details panel (design outreach-v2 t2-t4): the strongest news as the
 * headline, the reply with "Answer in Inbox", and the timeline of the pitch with the waiting
 * follow-up and its "Cancel the follow-up".
 */
const PitchStatusCardComponent: React.FC<PitchStatusCardProps> = ({
  row,
  pitch,
  name,
  campaignUuid,
  disabled,
  onStopFollowUp,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const hero = pitchHero(intl, row, pitch, name);
  const steps = useMemo(() => (pitch === undefined ? [] : pitchTimeline(intl, pitch)), [intl, pitch]);
  const reply = pitch === undefined ? null : newestReply(pitch);
  return (
    <section
      className={clsx(styles.hero, hero.tone === 'vio' && styles.heroVio, hero.tone === 'pos' && styles.heroPos)}
      data-testid="pitch-status"
    >
      <div>
        <h3 className={styles.heroTitle}>{hero.title}</h3>
        {hero.sub !== null && <p className={styles.heroSub}>{hero.sub}</p>}
      </div>
      {reply !== null && reply.snippet !== null && reply.snippet !== '' && (
        <div className={styles.reply}>
          <span>{intl.formatMessage(liveMessages.quoted, { text: reply.snippet })}</span>
          <span className={styles.replyMeta}>
            {intl.formatDate(new Date(reply.at), {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      )}
      {hero.replyUuid !== null && (
        <div>
          <SMLButton
            color="primary"
            size="sm"
            omitCap
            leftIcon={faReply}
            // eslint-disable-next-line no-void
            onClick={() => void navigate(outreachReplyPath(hero.replyUuid ?? ''))}
          >
            {intl.formatMessage(detailsMessages.answerInInbox)}
          </SMLButton>
        </div>
      )}
      {hero.review && (
        <div>
          <SMLButton
            size="sm"
            outline
            omitCap
            leftIcon={faPen}
            // eslint-disable-next-line no-void
            onClick={() => void navigate(outreachReviewPath(campaignUuid))}
          >
            {intl.formatMessage(detailsMessages.readPitches)}
          </SMLButton>
        </div>
      )}
      {steps.length > 0 && (
        <PitchTimelineList
          steps={steps}
          followUpAction={
            <SMLButton
              size="sm"
              color="link"
              omitCap
              className="p-0"
              disabled={disabled}
              onClick={() => onStopFollowUp(row.messageUuid)}
              testID="details-cancel-follow-up"
            >
              {intl.formatMessage(peopleMessages.cancelFollowUp)}
            </SMLButton>
          }
        />
      )}
    </section>
  );
};

export const PitchStatusCard = TypedMemo(PitchStatusCardComponent);
